import { darken } from "polished";
import React from "react";
import { IconType } from "react-icons/lib/cjs";
import { Link } from "react-router-dom";
import styled, { css, ThemedStyledProps } from "styled-components";
import {
  flexbox,
  FlexboxProps,
  layout,
  LayoutProps,
  space,
  SpaceProps,
} from "styled-system";
import { accessibleShade } from "../accessibleColor";

export type ButtonVariant = "primary" | "secondary" | "ghost";

type CommonProps = FlexboxProps &
  SpaceProps &
  LayoutProps & {
    block?: boolean;
    large?: boolean;
    small?: boolean;
    tiny?: boolean;
    disabled?: boolean;
    filled?: boolean;
    /**
     * primary: solid accent fill. secondary: white with a grey border.
     * ghost: text only. Defaults to primary when `filled`, else secondary.
     */
    variant?: ButtonVariant;
    color?: string;
    active?: boolean;
    name?: string;
    value?: string;
    style?: React.CSSProperties;
  };

/**
 * The button's colour name. variant="primary" means the accent, so it uses
 * the theme's primary colour unless a colour is given.
 */
const colorNameFromProps = (props: ThemedStyledProps<CommonProps, any>) =>
  props.color ||
  (props.variant === "primary" ? "primary" : props.theme.button.defaultColor);

const colorFromProps = (props: ThemedStyledProps<CommonProps, any>) =>
  (props.theme.colors[colorNameFromProps(props)] || props.color) as string;

type ThemedProps = ThemedStyledProps<CommonProps, any>;

const isFilled = (props: ThemedProps) =>
  Boolean(props.active || (props.filled ?? props.theme.button.defaultFilled));

/** Text colour on a filled button (white by default). */
const filledTextFromProps = (props: ThemedProps) =>
  props.theme.getSafeColor(props.theme.button.filledTextColor) as string;

/**
 * The fill for a filled button: the button's colour, deepened just enough for
 * its text to pass AA contrast. Light brand colours (orange, the user-area
 * blue) get a deeper shade instead of dark text.
 */
const fillFromProps = (props: ThemedProps) =>
  accessibleShade(colorFromProps(props), filledTextFromProps(props));

/** Outline buttons show their colour as text on white, so it must pass too. */
const outlineFromProps = (props: ThemedProps) =>
  accessibleShade(colorFromProps(props), "#fff");

/** Shades a colour, leaving gradients and other unparseable values alone. */
const shade = (c: string, amount: number) => {
  if (c.includes("(")) {
    return c;
  }
  try {
    return darken(amount, c);
  } catch {
    return c;
  }
};

const variantOf = (props: ThemedProps): ButtonVariant =>
  props.variant ?? (isFilled(props) ? "primary" : "secondary");

/**
 * Themes that predate the variants (e.g. Content Hub) keep the old outline
 * look: coloured border and text, filling in on hover.
 */
const usesNeutralSecondary = (props: ThemedProps) =>
  Boolean(props.theme.button.neutralSecondary);

/** Colours that read as "default" rather than carrying a meaning. */
const NEUTRAL_COLORS = new Set([
  "primary",
  "secondary",
  "body",
  "black",
  "blue",
  "hub",
  "grey",
  "grey0",
  "grey1",
  "grey2",
]);

/**
 * Text on a secondary button: the normal text colour, unless the button has
 * a meaningful colour (danger, positive...) which it keeps on its text.
 */
const secondaryTextFromProps = (props: ThemedProps) => {
  const name = colorNameFromProps(props);
  return NEUTRAL_COLORS.has(name)
    ? props.theme.colors.text ?? props.theme.colors.black
    : outlineFromProps(props);
};

const neutral = (props: ThemedProps, name: string, fallback: string) =>
  props.theme.colors[name] ?? fallback;

type Look = { bg: string; border: string; text: string };

/** Resting, hover and pressed colours for each variant. */
const lookFor = (
  props: ThemedProps,
  state: "rest" | "hover" | "pressed",
): Look => {
  const variant = variantOf(props);

  if (variant === "primary") {
    const fill = fillFromProps(props);
    const bg =
      state === "rest"
        ? fill
        : shade(fill, state === "hover" ? 0.05 : 0.1);
    return { bg, border: bg, text: filledTextFromProps(props) };
  }

  if (variant === "ghost") {
    return {
      bg:
        state === "rest"
          ? "transparent"
          : neutral(props, state === "hover" ? "grey6" : "grey5", "#f3f3f3"),
      border: "transparent",
      text: outlineFromProps(props),
    };
  }

  // Secondary.
  if (!usesNeutralSecondary(props)) {
    // Legacy outline: coloured border and text, fills in on hover.
    if (state === "rest") {
      return {
        bg: "white",
        border: outlineFromProps(props),
        text: outlineFromProps(props),
      };
    }
    const fill = fillFromProps(props);
    const bg = state === "hover" ? shade(fill, 0.05) : shade(fill, 0.1);
    return { bg, border: bg, text: filledTextFromProps(props) };
  }

  const border = neutral(props, "borderStrong", "#ccc");
  return {
    bg:
      state === "rest"
        ? neutral(props, "surface", "#fff")
        : neutral(props, state === "hover" ? "grey6" : "grey5", "#f3f3f3"),
    border: state === "pressed" ? shade(border, 0.05) : border,
    text: secondaryTextFromProps(props),
  };
};

const heightFor = (props: ThemedProps) =>
  props.small || props.tiny ? 32 : props.large ? 40 : 36;

/**
 * Button text is centred geometrically, but most of a label is lowercase,
 * which sits in the lower part of the line, so labels look bottom-heavy.
 * Moving 0.05em of padding from the top to the bottom lifts the text about
 * a pixel, which reads as centred, without changing the button's height.
 */
const OPTICAL_LIFT = "0.05em";

const paddingFor = (props: ThemedProps) => {
  const [v, h] =
    variantOf(props) === "ghost"
      ? [4, 8]
      : props.small || props.tiny
        ? [5, 12]
        : props.large
          ? [8, 20]
          : [6, 14];
  return `calc(${v}px - ${OPTICAL_LIFT}) ${h}px calc(${v}px + ${OPTICAL_LIFT})`;
};

const common = css<CommonProps>`
  display: ${(props) => (props.block ? "flex" : "inline-flex")};
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: ${heightFor}px;
  padding: ${paddingFor};
  margin-bottom: 0;
  font-weight: 600;
  font-size: ${(props) => (props.large ? 16 : props.tiny ? 12 : 14)}px;
  line-height: 1.25;
  text-align: center;
  vertical-align: middle;
  text-decoration: none;
  white-space: nowrap;
  touch-action: manipulation;
  cursor: pointer;
  user-select: none;
  background-image: none;
  border: ${(props) =>
    colorFromProps(props).includes("(") && variantOf(props) === "primary"
      ? "none"
      : `1px solid ${lookFor(props, "rest").border}`};
  border-radius: ${(props) =>
    typeof props.theme.button.borderRadius === "number"
      ? `${props.theme.button.borderRadius}px`
      : props.theme.button.borderRadius};
  background: ${(props) => lookFor(props, "rest").bg};
  color: ${(props) => lookFor(props, "rest").text};
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;
  ${flexbox};
  ${space};
  ${layout};

  &[disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not([disabled]) {
    background: ${(props) => lookFor(props, "hover").bg};
    border-color: ${(props) => lookFor(props, "hover").border};
    color: ${(props) => lookFor(props, "hover").text};
    text-decoration: none;
  }

  /* Pressed: a shade darker, nudged down. */
  &:active:not([disabled]) {
    transform: translateY(1px);
    transition-duration: 0.05s;
    background: ${(props) => lookFor(props, "pressed").bg};
    border-color: ${(props) => lookFor(props, "pressed").border};
    color: ${(props) => lookFor(props, "pressed").text};
  }

  &:focus {
    outline: 0;
  }

  /* Keyboard users need to see where focus is. */
  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.grey0};
    outline-offset: 2px;
  }
`;

export const ButtonElem = styled.button<CommonProps>`
  ${common};
`;

const LinkElem = styled(Link)`
  ${common};
`;

const AnchorElem = styled.a`
  ${common};
`;

type ButtonProps = CommonProps &
  React.AriaAttributes & {
  to?: string;
  href?: string;
  target?: string;
  icon?: IconType;
  children?: React.ReactNode | React.ReactNode[];
  type?: "button" | "submit";
  onClick?: (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => void;
};

const Button = ({
  to,
  href,
  icon: Icon,
  children,
  type,
  target,
  disabled,
  name,
  value,
  ...rest
}: ButtonProps) => {
  if (to != null && to.startsWith("http")) {
    href = to;
  }

  if (disabled) {
    to = undefined;
    href = undefined;
  }

  if (href != null) {
    return (
      <AnchorElem href={href} {...rest} target={target}>
        {children}
        {Icon && <Icon aria-hidden="true" />}
      </AnchorElem>
    );
  }

  if (to != null) {
    return (
      <LinkElem to={to} {...rest}>
        <>
          {children}
          {Icon && <Icon aria-hidden="true" />}
        </>
      </LinkElem>
    );
  }

  return (
    <ButtonElem
      type={type}
      {...rest}
      disabled={disabled}
      name={name}
      value={value}
    >
      {children}
        {Icon && <Icon aria-hidden="true" />}
    </ButtonElem>
  );
};

Button.defaultProps = {};

export default Button;
